import { tz } from "@date-fns/tz";
import { format, isValid, parse, parseISO } from "date-fns";

const UTC = tz("UTC");

const ISO_DATE_REGEX =
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

export function tryFormatIsoDate(value: string): string {
  if (!ISO_DATE_REGEX.test(value)) {
    return value;
  }

  const date = parseISO(value);

  return isValid(date) ? formatIsoDate(value) : value;
}

export function formatIsoDate(
  isoString: string | undefined | null,
  swap: boolean = true,
  includeTime: boolean = true
): string {
  if (!isoString) {
    return "";
  }
  const date = new Date(isoString);
  let pattern: string;

  if (!includeTime) {
    pattern = "dd-MMM-yyyy";
  } else {
    pattern = swap ? "dd-MMM-yyyy, HH:mm" : "HH:mm, dd-MMM-yyyy";
  }

  return format(date, pattern).toUpperCase();
}

/**
 * Stores a date-picker day as UTC midnight of that calendar date, so
 * 21 Oct in Tbilisi (or anywhere else) is always `2026-10-21T00:00:00.000Z`.
 */
export function toUtcCalendarIso(date: Date): string {
  return new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate())
  ).toISOString();
}

/** Formats a UTC calendar date without time or local-zone shift. */
export function formatUtcCalendarDate(
  isoString: string | undefined | null
): string {
  if (!isoString) {
    return "";
  }

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return format(date, "dd-MMM-yyyy", { in: UTC }).toUpperCase();
}

function toISODate(dateString: string, isEnd: boolean = false): string {
  const parsedDate: Date = parse(dateString.trim(), "MMMM d, yyyy", new Date());

  const utcDate = new Date(
    Date.UTC(
      parsedDate.getFullYear(),
      parsedDate.getMonth(),
      parsedDate.getDate(),
      isEnd ? 23 : 0,
      isEnd ? 59 : 0,
      isEnd ? 59 : 0,
      0
    )
  );

  return utcDate.toISOString().replace(".000", "");
}

export function parseDateRange(range: string | null | undefined): {
  dateFrom: string | null;
  dateTo: string | null;
} {
  if (!range || !range.includes(" - ")) {
    return { dateFrom: null, dateTo: null };
  }

  const [fromStr, toStr] = range.split(" - ");

  const dateFrom = fromStr ? toISODate(fromStr, false) : null;
  const dateTo = toStr ? toISODate(toStr, true) : null;

  return { dateFrom, dateTo };
}
